import { useState } from "react";
import { Plus, Pencil, Trash2, FileText } from "lucide-react";
import ButtonWrapper from "../../components/reuseComp/ButtonWrapper";
import {
  Button, Form, Input, InputNumber, Select, DatePicker, Modal,
  Table, Tag, Popconfirm, message, Checkbox,
} from "antd";
import type { ColumnType } from "antd/es/table";
import dayjs from "dayjs";
import { useContractList, useContractCreate, useContractUpdate, useContractDelete } from "../../../hooks/api/contracts/hooks";
import { IContract, IContractCreatePayload, IContractUpdatePayload } from "../../../hooks/api/contracts/callbacks";
import { useClientList } from "../../../hooks/api/clients/hooks";

// Must match the `contracts.status` enum in the DB exactly — "cancelled"
// and "renewed" were never valid values here (the table only has draft /
// active / expired / terminated / pending_renewal) and picking either one
// crashed contract save with a raw "Data truncated for column 'status'"
// MySQL error.
const STATUS_OPTS = [
  { value: "draft", label: "Draft" },
  { value: "active", label: "Active" },
  { value: "pending_renewal", label: "Pending Renewal" },
  { value: "expired", label: "Expired" },
  { value: "terminated", label: "Terminated" },
];

const STATUS_COLOR: Record<string, string> = {
  draft: "default",
  active: "green",
  pending_renewal: "gold",
  expired: "orange",
  terminated: "red",
};

const BILLING_FREQUENCY_OPTS = [
  { value: "monthly", label: "Monthly" },
  { value: "quarterly", label: "Quarterly" },
  { value: "annually", label: "Annually" },
  { value: "job_based", label: "Job-based" },
];

function fmt(v: number | null | undefined) {
  if (!v) return "—";
  return `£${Number(v).toLocaleString("en-GB", { minimumFractionDigits: 2 })}`;
}

function ContractModal({
  open, editing, onClose, onSuccess,
}: { open: boolean; editing: IContract | null; onClose: () => void; onSuccess: () => void }) {
  const [form] = Form.useForm();
  const [msgApi, ctx] = message.useMessage();
  const isEdit = !!editing;

  const { data: clientRes } = useClientList({ page: 1, limit: 200 });
  const clients = (clientRes?.data ?? []) as any[];

  const create = useContractCreate({
    onSuccess: () => { msgApi.success("Contract created."); onSuccess(); },
    onError: (e: any) => msgApi.error(e?.response?.data?.message || "Failed"),
  });

  const update = useContractUpdate({
    onSuccess: () => { msgApi.success("Contract updated."); onSuccess(); },
    onError: (e: any) => msgApi.error(e?.response?.data?.message || "Failed"),
  });

  const handleOpen = (v: boolean) => {
    if (!v) return;
    if (isEdit && editing) {
      form.setFieldsValue({
        ...editing,
        auto_renew: !!editing.auto_renew,
        start_date: editing.start_date ? dayjs(editing.start_date) : null,
        end_date: editing.end_date ? dayjs(editing.end_date) : null,
      });
    } else {
      form.resetFields();
      form.setFieldsValue({ status: "draft", billing_frequency: "monthly", renewal_reminder_days: 60, auto_renew: false });
    }
  };

  const handleSubmit = () => {
    form.validateFields().then((vals) => {
      if (vals.start_date && vals.end_date && vals.end_date.isBefore(vals.start_date, "day")) {
        msgApi.error("End date cannot be before start date.");
        return;
      }
      const payload = {
        ...vals,
        start_date: vals.start_date ? vals.start_date.format("YYYY-MM-DD") : null,
        end_date: vals.end_date ? vals.end_date.format("YYYY-MM-DD") : null,
      };
      if (isEdit && editing) {
        update.mutate({ ...payload, id: editing.id } as IContractUpdatePayload);
      } else {
        create.mutate(payload as IContractCreatePayload);
      }
    });
  };

  return (
    <Modal
      open={open}
      onCancel={onClose}
      afterOpenChange={handleOpen}
      title={isEdit ? `Edit Contract` : "New Contract"}
      width={600}
      footer={null}
      destroyOnClose
    >
      {ctx}
      <Form form={form} layout="vertical" size="small" className="mt-3">
        <Form.Item name="client_id" label="Client" rules={[{ required: true, message: "Client is required" }]}>
          <Select
            showSearch
            placeholder="Select client…"
            filterOption={(v, o) => (o?.label as string)?.toLowerCase().includes(v.toLowerCase())}
            options={clients.map((c: any) => ({
              value: c.id,
              label: c.business_name || `${c.first_name ?? ""} ${c.last_name ?? ""}`.trim() || c.email || `Client #${c.id}`,
            }))}
          />
        </Form.Item>
        <div className="grid grid-cols-2 gap-x-4">
          <Form.Item name="title" label="Contract Title" rules={[{ required: true, message: "Title is required" }]}>
            <Input placeholder="e.g. Annual Pest Control Agreement" />
          </Form.Item>
          <Form.Item name="reference" label="Reference">
            <Input placeholder="e.g. CT-0042" />
          </Form.Item>
        </div>
        <div className="grid grid-cols-2 gap-x-4">
          <Form.Item name="start_date" label="Start Date">
            <DatePicker className="w-full" format="DD/MM/YYYY" />
          </Form.Item>
          <Form.Item name="end_date" label="End Date">
            <DatePicker className="w-full" format="DD/MM/YYYY" />
          </Form.Item>
        </div>
        <div className="grid grid-cols-2 gap-x-4">
          <Form.Item name="value" label="Contract Value">
            <InputNumber className="w-full" min={0} step={0.01} prefix="£" placeholder="0.00" />
          </Form.Item>
          <Form.Item name="billing_frequency" label="Billing Frequency" initialValue="monthly">
            <Select options={BILLING_FREQUENCY_OPTS} />
          </Form.Item>
        </div>
        <Form.Item name="status" label="Status" initialValue="draft">
          <Select options={STATUS_OPTS} />
        </Form.Item>
        <div className="grid grid-cols-2 gap-x-4 items-center">
          <Form.Item name="auto_renew" valuePropName="checked" initialValue={false}>
            <Checkbox>Auto-renew</Checkbox>
          </Form.Item>
          <Form.Item
            name="renewal_reminder_days"
            label="Renewal reminder (days before end date)"
            initialValue={60}
          >
            <InputNumber className="w-full" min={0} />
          </Form.Item>
        </div>
        <Form.Item name="notes" label="Notes">
          <Input.TextArea rows={2} placeholder="Optional notes…" />
        </Form.Item>
        <Form.Item name="document_url" label="Document URL">
          <Input placeholder="https://…" />
        </Form.Item>
        <div className="flex justify-end gap-2 mt-2">
          <Button onClick={onClose}>Cancel</Button>
          <Button type="primary" loading={create.isPending || update.isPending} onClick={handleSubmit}>
            {isEdit ? "Save Changes" : "Create Contract"}
          </Button>
        </div>
      </Form>
    </Modal>
  );
}

const ContractsPage = () => {
  const [page, setPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState<string | undefined>();
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<IContract | null>(null);
  const [msgApi, ctx] = message.useMessage();

  const { data, isLoading, refetchWithQuery } = useContractList({ page, limit: 20 });
  const contracts: IContract[] = (data?.data ?? []) as IContract[];
  const total = Number((data as any)?.extra?.total ?? 0);

  const del = useContractDelete({
    onSuccess: () => { msgApi.success("Contract deleted."); refetchWithQuery({ page }); },
    onError: (e: any) => msgApi.error(e?.response?.data?.message || "Failed"),
  });

  const columns: ColumnType<IContract>[] = [
    {
      title: "Contract",
      key: "title",
      render: (_, r) => (
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: "rgba(1,59,190,0.08)" }}>
            <FileText size={14} style={{ color: "var(--color-primary)" }} />
          </div>
          <div>
            <div className="font-medium text-slate-800 text-sm">{r.title}</div>
            {r.client_name && <div className="text-xs text-slate-400">{r.client_name}</div>}
          </div>
        </div>
      ),
    },
    { title: "Value", dataIndex: "value", key: "value", render: (v) => <span className="font-medium">{fmt(v)}</span> },
    { title: "Start", dataIndex: "start_date", key: "start_date", render: (v) => v ? dayjs(v).format("DD MMM YYYY") : "—" },
    {
      title: "End",
      dataIndex: "end_date",
      key: "end_date",
      render: (v) => {
        if (!v) return "—";
        const expired = dayjs(v).isBefore(dayjs(), "day");
        const soon = !expired && dayjs(v).diff(dayjs(), "day") < 30;
        return <span className={expired ? "text-red-600 font-medium" : soon ? "text-amber-600 font-medium" : ""}>{dayjs(v).format("DD MMM YYYY")}</span>;
      },
    },
    { title: "Status", dataIndex: "status", key: "status", render: (v) => <Tag color={STATUS_COLOR[v] ?? "default"}>{v}</Tag> },
    {
      title: "Actions", key: "actions",
      render: (_, r) => (
        <div className="flex gap-1">
          <Button size="small" icon={<Pencil size={11} />} onClick={() => { setEditing(r); setModalOpen(true); }} />
          <Popconfirm title="Delete this contract?" onConfirm={() => del.mutate(r.id)} okButtonProps={{ danger: true }}>
            <Button size="small" icon={<Trash2 size={11} />} danger type="text" />
          </Popconfirm>
        </div>
      ),
    },
  ];

  return (
    <div className="p-6 min-h-screen">
      {ctx}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-xl font-semibold text-slate-800">Contracts</h1>
          <p className="text-slate-500 text-sm">{total} total contracts</p>
        </div>
        <ButtonWrapper type="primary" icon={<Plus size={14} />} onClick={() => { setEditing(null); setModalOpen(true); }} className="flex items-center gap-2">
          New Contract
        </ButtonWrapper>
      </div>

      <div className="flex gap-3 mb-4 flex-wrap">
        {[undefined, "draft", "active", "pending_renewal", "expired", "terminated"].map((s) => (
          <button key={s ?? "all"} onClick={() => { setStatusFilter(s); refetchWithQuery({ page: 1, status: s }); setPage(1); }}
            className={`px-3 py-1 text-xs rounded-full border font-medium transition-colors ${statusFilter === s ? "!bg-primary text-white !border-primary" : "bg-white text-slate-600 border-slate-200 hover:border-primary"}`}>
            {s ? s.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase()) : "All"}
          </button>
        ))}
      </div>

      <div className="bg-white rounded-xl border border-slate-100 shadow-sm overflow-hidden">
        <Table dataSource={contracts} columns={columns} rowKey="id" loading={isLoading}
          pagination={{ current: page, pageSize: 20, total, showSizeChanger: false, showTotal: (t) => `${t} contracts`, onChange: (p) => { setPage(p); refetchWithQuery({ page: p, status: statusFilter }); } }}
          scroll={{ x: 700 }} />
      </div>

      <ContractModal open={modalOpen} editing={editing}
        onClose={() => { setModalOpen(false); setEditing(null); }}
        onSuccess={() => { setModalOpen(false); setEditing(null); refetchWithQuery({ page }); }} />
    </div>
  );
};

export default ContractsPage;
